import React, { useState, useEffect } from 'react'
import { useHistory } from "react-router";

import { usePersistedState } from "hooks";
import { entities } from "reduxStore";

import Api from "../../../api";
import { SecondStep, ExitPopup, BackendJob, EditedJob, EditPostMode } from "components/EditJob";
import ChooseDescriptionStep from "components/EditJob/ChooseDescriptionStep";
import FixedLoader from "components/FixedLoader";
import useActionApi from "../../../hooks/useActionApi";

const MINIMUM_TITLE_LENGTH = 5;
const MINIMUM_DESCRIPTION_LENGTH = 15;

export default function EditJobPostSecondStep() {
	const history = useHistory()
	const { mode, originalJob } = history.location.state as { mode: EditPostMode, originalJob?: BackendJob }
	

	const [persistedJob, saveJob] = usePersistedState({
		entityName: entities.editedJobPost,
		initialValue: {} as EditedJob,
	})

	const [isExitPopupOpen, setIsExitPopupOpen] = useState(false);
	const [isChooseDescriptionOpen, setIsChooseDescriptionOpen] = useState(false)

	const [titleValue, setTitleValue] = useState(persistedJob.title ? persistedJob.title : undefined)
	const [titleValueError, setTitleValueError] = useState('');

	const [descriptionValue, setDescriptionValue] = useState( persistedJob.description ? persistedJob.description : undefined)
	const [descriptionValueError, setDescriptionValueError] = useState('');
	const [practiceName, setPracticeName] = useState( persistedJob.location?.practiceName ? persistedJob.location?.practiceName : undefined)

	const [spellCheckedResponse, setSpellCheckedResponse] = useState('');
    const [finalResponse, setFinalResponse] = useState('');

	const [checkJobDescriptionInfoLoading, , checkJobDescriptionInfo] = useActionApi({
		apiMethod: Api.checkJobDescriptionInfo,
		onSuccess: (message: { spellChecked: string, final: string, error?: boolean, message?: string }) => {
		  if (message.error) {
			setDescriptionValueError(message.message || 'An error occurred while processing your job description.');
			setIsChooseDescriptionOpen(false);
			return;
		  }
	  
		  if (message.spellChecked && message.final) {
			setSpellCheckedResponse(message.spellChecked);
			setFinalResponse(message.final);
			setIsChooseDescriptionOpen(true);
		  } else {
			console.error('Invalid message received:', message);
			setDescriptionValueError('An unexpected error occurred. Please try again.');
			setIsChooseDescriptionOpen(false);
		  }
		},
		onError: (error: any) => {
		  console.error('There was an error!', error);
		  setDescriptionValueError('An error occurred while processing your request. Please try again later.');
		  setIsChooseDescriptionOpen(false);
		}
	  });

	const onSetTitleValue = (value: string) => {
		titleValueError && value.length >= MINIMUM_TITLE_LENGTH && setTitleValueError('');
		setTitleValue(value);
	}
	const onSetDescriptionValue = (value: string) => {
		descriptionValueError && value.length >= MINIMUM_DESCRIPTION_LENGTH && setDescriptionValueError('');
		setDescriptionValue(value);
	}

	const onJobSave = (descriptionValue: string) => {
		saveJob({
			...persistedJob,
			...(titleValue && { title: titleValue }),
			...(descriptionValue && { description: descriptionValue }),
		})
	}

	const onClose = () => {
		setIsExitPopupOpen(true);
	}

	const onBack = () => {
		onJobSave("");
		history.push( { pathname: "editJobFirstStep", state: { mode, originalJob } })
	}

	const onSave = (description: string) => {
		onJobSave(description);
		history.push({ pathname: "editJobOverviewStep", state: { mode, originalJob } })
	}

	const onExitPopupClose = () => {
		setIsExitPopupOpen(false);
	}
	const onExitPopupConfirm = () => {
		setIsExitPopupOpen(false);

		if (mode === EditPostMode.edit) {
			history.push({ pathname: "jobBoardDetails", state: { job: originalJob } });
		} else {
			history.replace("jobBoard")
		}
	}

	const onSubmit = async (event: any = { preventDefault: () => {} }) => {
		event.preventDefault();

		let canContinue = true;
		if (!titleValue || titleValue.length < MINIMUM_TITLE_LENGTH) {
			canContinue = false;
			setTitleValueError(`You must enter at least ${MINIMUM_TITLE_LENGTH} characters to continue`)
		}
		if (!descriptionValue || descriptionValue.length < MINIMUM_DESCRIPTION_LENGTH) {
			canContinue = false;
			setDescriptionValueError(`You must enter at least ${MINIMUM_DESCRIPTION_LENGTH} characters to continue`)
		}

		if (canContinue) {
			checkJobDescriptionInfo(descriptionValue as string);
        }
	}

	return (
		<>
			{isChooseDescriptionOpen ?
				<ChooseDescriptionStep
					isOpen={isChooseDescriptionOpen}
					spellCheckedResponse={spellCheckedResponse}
					finalResponse={finalResponse}
					onBack={() => setIsChooseDescriptionOpen(false)}
					onSave={onSave}
				/>
				: <SecondStep
					titleValue={titleValue}
					titleValueError={titleValueError}
					onTitleValueChange={onSetTitleValue}
					descriptionValue={descriptionValue}
					descriptionValueError={descriptionValueError}
					onDescriptionValueChange={onSetDescriptionValue}
					practiceName={practiceName}
					onBack={onBack}
					onClose={onClose}
					onSubmit={onSubmit}
				/>}
        <ExitPopup opened={isExitPopupOpen} onConfirm={onExitPopupConfirm} onClose={onExitPopupClose} />
        <FixedLoader loading={checkJobDescriptionInfoLoading} />
		</>
	)
}
